/**
 * Reconnect Modal
 * Handles session reconnection after expiry
 */

import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('Reconnect');

/**
 * Show reconnect modal (for session expired scenarios)
 */
export async function showReconnectModal(): Promise<void> {
  try {
    const html = await loadOEMTemplate('/forms/auth/reconnect.html');

    ModalSystem.show({
      title: 'Session Expired',
      html,
      width: '500px',
      onOpened: () => {
        setupReconnectForm();
      }
    });
  } catch (error) {
    logger.error('Error loading reconnect form:', error);
    ToasterService.error('Failed to load reconnect form');
  }
}

/**
 * Setup the reconnect form submission handler
 */
function setupReconnectForm(): void {
  const form = document.getElementById('reconnect-form') as HTMLFormElement;
  if (!form) {
    logger.warn('Reconnect form not found');
    return;
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    const password = formData.get('password') as string;

    if (!password) {
      ToasterService.warning('Please enter your password');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Reconnecting...';

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      ToasterService.success('Session reconnected successfully');
      ModalSystem.hide();
    } catch (error) {
      logger.error('Failed to reconnect:', error);
      ToasterService.error('Failed to reconnect. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
